import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ensureApiJwtTokenRefreshed } from '../../../../../context/AuthContext';
import FlairCard from '../../../../../atomic/templates/FlairCard/FlairCard';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import './LightningOutWidget.css';

const i18Path = 'dashboard.lightningOutWidget';

const LOCALE = 'en-US';
const LIGHTNING_OUT_SCRIPT_SELECTOR = 'script[src*="lightning.out.latest"]';
const LIGHTNING_OUT_SCRIPT_PATH =
  '/lightning/lightning.out.latest/index.iife.prod.js';
const SF_FRONTDOOR_ENDPOINT = '/api/sf-frontdoor';

type FrontdoorResponse = {
  frontdoorUrl: string;
};

const fetchFrontdoorUrl = async (
  lightningAppId: string,
): Promise<string | null> => {
  const token = await ensureApiJwtTokenRefreshed();
  if (!token) return null;

  const url = `${SF_FRONTDOOR_ENDPOINT}?lightningAppId=${encodeURIComponent(
    lightningAppId,
  )}`;
  const res = await fetch(url, {
    headers: { Authorization: token },
    credentials: 'include',
  });
  if (!res.ok) return null;

  const data: FrontdoorResponse = await res.json();
  return data.frontdoorUrl;
};

type WidgetState = {
  scriptLoaded: boolean;
  error: string | null;
  frontdoorUrl: string | null;
  apiToken: string | null | undefined;
};

const INITIAL_WIDGET_STATE: WidgetState = {
  scriptLoaded: false,
  error: null,
  frontdoorUrl: null,
  apiToken: undefined,
};

type LightningOutWidgetProps = {
  flowApiName?: string;
  lwcComponentName?: string;
  sfInstanceUrl?: string;
  lightningAppId?: string;
};

const LightningOutWidget: React.FC<LightningOutWidgetProps> = ({
  flowApiName = '',
  lwcComponentName,
  sfInstanceUrl: sfInstanceUrlProp,
  lightningAppId,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const loadedInstanceUrlRef = useRef<string | null>(null);
  const [state, setState] = useState<WidgetState>(INITIAL_WIDGET_STATE);

  const instanceUrl = sfInstanceUrlProp;

  useEffect(() => {
    if (state.apiToken === undefined) {
      ensureApiJwtTokenRefreshed().then((token) =>
        setState((prev) => ({ ...prev, apiToken: token })),
      );
    }
  }, [state.apiToken]);

  const hubJwtToken = state.apiToken
    ? state.apiToken.replace(/^Bearer\s+/i, '').trim()
    : null;

  useEffect(() => {
    if (!instanceUrl) return;
    if (loadedInstanceUrlRef.current === instanceUrl) return;

    setState((prev) => ({
      ...prev,
      scriptLoaded: false,
      error: null,
      frontdoorUrl: null,
    }));
    loadedInstanceUrlRef.current = instanceUrl;

    const existing = document.querySelector(LIGHTNING_OUT_SCRIPT_SELECTOR);
    if (existing) {
      if (
        typeof window !== 'undefined' &&
        window.customElements?.get('lightning-out-application')
      ) {
        setState((prev) => ({ ...prev, scriptLoaded: true }));
        return;
      }
      existing.remove();
    }

    const script = document.createElement('script');
    script.src = `${instanceUrl}${LIGHTNING_OUT_SCRIPT_PATH}`;
    script.async = true;
    script.onload = () => setState((prev) => ({ ...prev, scriptLoaded: true }));
    script.onerror = () =>
      setState((prev) => ({ ...prev, error: t('failedToLoadScript') }));
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [instanceUrl, t]);

  const fetchFrontdoor = useCallback(async () => {
    if (!state.scriptLoaded || state.frontdoorUrl || !lightningAppId) return;
    try {
      const url = await fetchFrontdoorUrl(lightningAppId);
      setState((prev) => ({ ...prev, frontdoorUrl: url }));
    } catch {
      setState((prev) => ({ ...prev, error: t('failedToLoad') }));
    }
  }, [state.scriptLoaded, state.frontdoorUrl, lightningAppId, t]);

  useEffect(() => {
    fetchFrontdoor();
  }, [fetchFrontdoor]);

  if (state.error) {
    return (
      <FlairCard>
        <div className="lightning-out-error">
          {t('errorLabel')}: {state.error}
        </div>
      </FlairCard>
    );
  }
  if (!instanceUrl || !hubJwtToken || !state.frontdoorUrl) return null;

  return (
    <div>
      <lightning-out-application
        components="c-dynamic-lightning-out-h-u-b-component-container"
        frontdoor-url={state.frontdoorUrl}
        locale={LOCALE}
      />
      <div className="lightning-out-widget-container">
        {lwcComponentName ? (
          <c-dynamic-lightning-out-h-u-b-component-container
            component-name={lwcComponentName}
            token={hubJwtToken}
          />
        ) : (
          <c-dynamic-lightning-out-h-u-b-component-container
            flow-api-name={flowApiName}
            token={hubJwtToken}
          />
        )}
      </div>
    </div>
  );
};

export default LightningOutWidget;
